import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Button,
  Chip,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { Link } from 'react-router-dom';

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  filterControl: {
    minWidth: 200,
  },
  table: {
    minWidth: 650,
  },
  activeChip: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  inactiveChip: {
    backgroundColor: '#9e9e9e',
    color: 'white',
  },
  eventChip: {
    marginRight: theme.spacing(0.5),
  },
  readinessCompleted: {
    backgroundColor: '#2196f3',
    color: 'white',
  },
  partialSalePublished: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  login: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  inactivity: {
    backgroundColor: '#9c27b0',
    color: 'white',
  },
  vixTierChange: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
});

const EVENT_TYPES = {
  readiness_completed: 'Readiness Completed',
  partial_sale_published: 'Partial Sale Published',
  login: 'User Login',
  inactivity_7days: '7-Day Inactivity',
  inactivity_30days: '30-Day Inactivity',
  vix_tier_change: 'VIX Tier Change',
};

const TriggerList = ({ classes, triggers, onToggleActive, onDelete, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [actionTypeFilter, setActionTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredTriggers = triggers.filter(trigger => {
    const matchesSearch = trigger.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEventType = eventTypeFilter === 'all' || trigger.eventType === eventTypeFilter;
    const matchesActionType = actionTypeFilter === 'all' || trigger.actionType === actionTypeFilter;
    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && trigger.active) ||
                         (statusFilter === 'inactive' && !trigger.active);
    return matchesSearch && matchesEventType && matchesActionType && matchesStatus;
  });

  const handleToggleActive = (triggerId, currentActive) => {
    if (onToggleActive) {
      onToggleActive(triggerId, !currentActive);
    }
  };

  const handleDelete = (triggerId, triggerName) => {
    if (window.confirm(`Are you sure you want to delete the trigger "${triggerName}"? This action cannot be undone.`)) {
      if (onDelete) {
        onDelete(triggerId);
      }
    }
  };

  const getEventChipClass = (eventType) => {
    if (eventType.includes('readiness')) return classes.readinessCompleted;
    if (eventType.includes('partial')) return classes.partialSalePublished;
    if (eventType.includes('login')) return classes.login;
    if (eventType.includes('inactivity')) return classes.inactivity;
    if (eventType.includes('vix')) return classes.vixTierChange;
    return '';
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4">Email Triggers</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          component={Link}
          to="/admin/email_triggers/new"
        >
          Create Trigger
        </Button>
      </div>

      <div className={classes.filters}>
        <TextField
          label="Search triggers"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.filterControl}
        />
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Event Type</InputLabel>
          <Select
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            label="Event Type"
          >
            <MenuItem value="all">All Events</MenuItem>
            {Object.entries(EVENT_TYPES).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Action Type</InputLabel>
          <Select
            value={actionTypeFilter}
            onChange={(e) => setActionTypeFilter(e.target.value)}
            label="Action Type"
          >
            <MenuItem value="all">All Actions</MenuItem>
            <MenuItem value="send_email">Send Email</MenuItem>
            <MenuItem value="add_to_sequence">Add to Sequence</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </div>

      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Event Type</TableCell>
              <TableCell>Action Type</TableCell>
              <TableCell>Template/Sequence</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTriggers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {triggers.length === 0
                      ? 'No triggers created yet. Click "Create Trigger" to get started.'
                      : 'No triggers match your filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTriggers.map(trigger => (
                <TableRow key={trigger.id}>
                  <TableCell>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {trigger.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={EVENT_TYPES[trigger.eventType] || trigger.eventType}
                      size="small"
                      className={`${classes.eventChip} ${getEventChipClass(trigger.eventType)}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {trigger.actionType === 'send_email' ? 'Send Email' : 'Add to Sequence'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {trigger.emailTemplate?.name || trigger.emailSequence?.name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={trigger.active ? 'Active' : 'Inactive'}
                      size="small"
                      className={trigger.active ? classes.activeChip : classes.inactiveChip}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={trigger.active}
                      onChange={() => handleToggleActive(trigger.id, trigger.active)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <div className={classes.actions}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          component={Link}
                          to={`/admin/email_triggers/${trigger.id}/edit`}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(trigger.id, trigger.name)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

TriggerList.propTypes = {
  classes: PropTypes.object.isRequired,
  triggers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      eventType: PropTypes.string.isRequired,
      actionType: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
      emailTemplate: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      emailSequence: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    })
  ).isRequired,
  onToggleActive: PropTypes.func,
  onDelete: PropTypes.func,
  onRefresh: PropTypes.func,
};

export default withStyles(styles)(TriggerList);

